import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Button, Portal, Tooltip } from "@fluentui/react-components";
import { QuestionCircle20Regular, QuestionCircle24Regular } from "@fluentui/react-icons";
import {
  groupStepsBySection,
  localizedContext,
  localizedStep,
  stepVisibleForViewer,
  type HelpLanguage,
  type HelpStep,
  type HelpViewer,
} from "./types";
import { helpForPath } from "./registry";
import { activeDialogSurface, isStepOnScreen } from "./dom";
import { revealHelpStep, revealRecipe, useRevealNavigation } from "./reveal";
import { HelpDrawer } from "./HelpDrawer";
import { HelpTour } from "./HelpTour";
import { HelpInspect } from "./HelpInspect";
import { readPreference, writePreference } from "./preference";
import { helpText } from "./strings";
import { AFTER_DRAWER_MS, Z } from "./ui";

interface Props {
  pathname: string;
  viewer: HelpViewer;
  /** The app's language; the guide follows it until the viewer picks another in the drawer. */
  language: HelpLanguage;
  /** Short, non-blocking message (the app's toast). */
  notify: (message: string) => void;
  /** Colour of the docked button, to match the other top-bar icons. */
  color?: string;
}

interface TourState {
  /** New for every start, so a restarted tour remounts at its own start index. */
  id: number;
  steps: HelpStep[];
  scope: HTMLElement | null;
  startIndex: number;
}

/**
 * The one owner of guide state, mounted once in the top bar. Receives plain
 * props; only the shell touches the router and the global store.
 */
export function HelpLauncher({ pathname, viewer, language: appLanguage, notify, color }: Props) {
  const page = helpForPath(pathname);
  // open = openFor === pathname, so navigating closes the drawer with no effect
  const [openFor, setOpenFor] = useState<string | null>(null);
  const [tour, setTour] = useState<TourState | null>(null);
  const [inspecting, setInspecting] = useState(false);
  const [floating, setFloating] = useState(() => readPreference("floating") === "1");
  const [language, setLanguage] = useState<HelpLanguage>(
    () => (readPreference("language") as HelpLanguage | null) ?? appLanguage
  );
  const t = helpText(language);
  const navigation = useRevealNavigation(language);
  const [dialogOpen, setDialogOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);
  const open = openFor === pathname;

  const steps = useMemo(
    () => (page ? page.steps.filter((s) => stepVisibleForViewer(s, viewer)) : []),
    [page, viewer]
  );

  // "Is a user dialog open?" — set state only on change, or every DOM mutation re-renders the launcher.
  useEffect(() => {
    const update = () => {
      const next = activeDialogSurface() !== null;
      setDialogOpen((prev) => (prev === next ? prev : next));
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // While a dialog is open the top bar sits under its backdrop: float a copy of the button above it.
  useLayoutEffect(() => {
    if (dialogOpen && buttonRef.current) setButtonRect(buttonRef.current.getBoundingClientRect());
  }, [dialogOpen]);

  useEffect(() => {
    setTour(null);
    setInspecting(false);
  }, [pathname]);

  // switching the app's language wins over an earlier choice in the drawer
  const appLanguageRef = useRef(appLanguage);
  useEffect(() => {
    if (appLanguageRef.current === appLanguage) return;
    appLanguageRef.current = appLanguage;
    setLanguage(appLanguage);
    writePreference("language", appLanguage);
  }, [appLanguage]);

  const changeLanguage = useCallback((next: HelpLanguage) => {
    setLanguage(next);
    writePreference("language", next);
  }, []);

  const changeFloating = useCallback((next: boolean) => {
    setFloating(next);
    writePreference("floating", next ? "1" : "0");
  }, []);

  const startTour = useCallback(
    (from?: HelpStep) => {
      if (!page) return;
      const wasOpen = openFor !== null;
      setOpenFor(null);
      setInspecting(false);
      const recipe = from ? revealRecipe(page, from) : undefined;
      const begin = (mayReveal: boolean) => {
        const surface = activeDialogSurface();
        // section order (= screen order), so the tour and its section arrows agree
        const visible = groupStepsBySection(
          steps.filter((s) => isStepOnScreen(s, surface ?? document)),
          page.sections
        ).flatMap((group) => group.steps);
        if (from && !visible.includes(from) && mayReveal && recipe) {
          // open the menu / tab / dialog the step lives in, then start there
          void revealHelpStep(page, from, navigation).then(() => begin(false));
          return;
        }
        if (visible.length === 0) {
          notify(
            surface
              ? t("help.notice.nothingInDialog", "This dialog has no guide steps.")
              : t("help.notice.nothingOnScreen", "None of this page's guide steps is on screen right now.")
          );
          return;
        }
        let startIndex = 0;
        if (from) {
          startIndex = visible.indexOf(from);
          if (startIndex < 0) {
            const title = localizedStep(from, language).value.title;
            const how = from.context ? localizedContext(page, from.context, language)?.value : null;
            notify(
              how
                ? t("help.notice.stepHiddenContext", "\"{{title}}\" is not on screen. {{how}}", { title, how })
                : t("help.notice.stepHidden", "\"{{title}}\" is not on screen right now.", { title })
            );
            return;
          }
        }
        setTour({ id: Date.now(), steps: visible, scope: surface, startIndex });
      };
      window.setTimeout(() => begin(true), wasOpen ? AFTER_DRAWER_MS : 0);
    },
    [page, steps, openFor, language, notify, t, navigation]
  );

  const closeDrawer = useCallback(() => setOpenFor(null), []);
  const closeTour = useCallback(() => setTour(null), []);
  const closeInspect = useCallback(() => setInspecting(false), []);

  // close drawer and tour first: the drawer would cover the page being pointed at
  const startInspect = useCallback(() => {
    const wasOpen = openFor !== null;
    setOpenFor(null);
    setTour(null);
    window.setTimeout(() => setInspecting(true), wasOpen ? AFTER_DRAWER_MS : 0);
  }, [openFor]);

  const continueFrom = useCallback(
    (step: HelpStep) => {
      setInspecting(false);
      startTour(step);
    },
    [startTour]
  );

  if (!page) return null;

  const label = t("help.launcher", "Help for this page");
  const button = (ref?: typeof buttonRef) => (
    <Tooltip content={label} relationship="label">
      <Button
        ref={ref}
        data-help="app-help-launcher"
        appearance="transparent"
        icon={<QuestionCircle20Regular />}
        aria-expanded={open}
        onClick={() => setOpenFor(open ? null : pathname)}
        style={{ color }}
      />
    </Tooltip>
  );

  return (
    <>
      <span style={{ visibility: dialogOpen && buttonRect ? "hidden" : "visible", display: "inline-flex" }}>
        {button(buttonRef)}
      </span>
      {dialogOpen && buttonRect && !open && (
        <Portal>
          <div
            data-help-panel="launcher"
            style={{
              position: "fixed",
              top: buttonRect.top,
              left: buttonRect.left,
              zIndex: Z.launcherCopy,
            }}
          >
            {button()}
          </div>
        </Portal>
      )}
      {floating && !open && (
        <Portal>
          <div
            data-help-panel="launcher"
            style={{ position: "fixed", right: 24, bottom: 24, zIndex: Z.launcherCopy }}
          >
            <Tooltip content={label} relationship="label">
              <Button
                data-help="app-help-fab"
                shape="circular"
                size="large"
                appearance="primary"
                icon={<QuestionCircle24Regular />}
                onClick={() => setOpenFor(pathname)}
                style={{ width: 48, minWidth: 48, maxWidth: 48, height: 48, boxShadow: "0 4px 12px rgba(0, 0, 0, 0.25)" }}
              />
            </Tooltip>
          </div>
        </Portal>
      )}
      <HelpDrawer
        open={open}
        page={page}
        steps={steps}
        viewer={viewer}
        language={language}
        onLanguageChange={changeLanguage}
        floating={floating}
        onFloatingChange={changeFloating}
        notify={notify}
        onClose={closeDrawer}
        onStartTour={startTour}
        onInspect={startInspect}
      />
      {inspecting && (
        <HelpInspect
          page={page}
          steps={steps}
          language={language}
          onContinue={continueFrom}
          onClose={closeInspect}
        />
      )}
      {tour && (
        <HelpTour
          key={tour.id}
          page={page}
          steps={tour.steps}
          scope={tour.scope}
          startIndex={tour.startIndex}
          language={language}
          onInspect={startInspect}
          onClose={closeTour}
        />
      )}
    </>
  );
}
